import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Button, Skeleton, Alert, Chip, useMediaQuery, useTheme, Dialog, DialogContent, AppBar, Toolbar } from '@mui/material';
import { Icon } from '@iconify/react';
export interface EntityField {
  label: string;
  value: React.ReactNode;
  type?: 'text' | 'chip' | 'date' | 'email' | 'phone' | 'url';
  chipColor?: string;
  chipBgColor?: string;
}
export interface EntitySection {
  title: string;
  fields: EntityField[];
}
export interface EntityViewDrawerProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  title: string;
  subtitle?: string;
  entityId: string | number;
  sections: EntitySection[];
  isLoading?: boolean;
  error?: string;
  onRetry?: () => void;
}
const EntityViewDrawer: React.FC<EntityViewDrawerProps> = ({
  open,
  onClose,
  onEdit,
  title,
  subtitle,
  entityId,
  sections,
  isLoading = false,
  error,
  onRetry
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(open);
  useEffect(() => {
    setIsOpen(open);
  }, [open]);
  const handleClose = () => {
    setIsOpen(false);
    setTimeout(onClose, 300); // Allow animation to complete
  };
  const renderFieldValue = (field: EntityField) => {
    if (field.type === 'chip') {
      return <Chip label={field.value} size="small" sx={{
        backgroundColor: field.chipBgColor || 'rgba(14, 165, 233, 0.1)',
        color: field.chipColor || '#0EA5E9',
        height: 24,
        fontWeight: 600
      }} data-id="element-2816" data-mp="11jgdocvmmydu" />;
    }
    if (field.type === 'date' && typeof field.value === 'string') {
      return new Date(field.value as string).toLocaleString();
    }
    return field.value;
  };
  const content = <>
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }} data-id="element-2817" data-mp="1kg7w6k3ch9gu">
        {/* Header */}
        <Box sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }} data-id="element-2818" data-mp="1z3yg3h0i5lhm">
          <Box data-id="element-2819" data-mp="1l58hty1y5fro">
            {isLoading ? <>
                <Skeleton width={180} height={32} data-id="element-2820" data-mp="1l4qaa59u3f4g" />
                {subtitle && <Skeleton width={120} height={24} data-id="element-2821" data-mp="1g6f1zyz6tcoc" />}
              </> : <>
                <Typography variant="h5" component="h2" fontWeight={600} data-id="element-2822" data-mp="1bf6vpbt5m7fe">
                  {title}
                </Typography>
                {subtitle && <Typography variant="body2" color="text.secondary" sx={{
              mt: 0.5
            }} data-id="element-2823" data-mp="12eqsm3gbp2b2">
                    {subtitle}
                  </Typography>}
              </>}
          </Box>
          <IconButton onClick={handleClose} aria-label="close" edge="end" sx={{
          ml: 2
        }} data-id="element-2824" data-mp="344pjmzw679eo">
            <Icon icon="lucide:x" width={24} height={24} data-id="element-2825" data-mp="3e2dhhyp2ox12" />
          </IconButton>
        </Box>
        {/* Content */}
        <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        p: 3
      }} data-id="element-2826" data-mp="3i3z102w449k8">
          {error ? <Alert severity="error" action={onRetry && <Button color="inherit" size="small" onClick={onRetry} data-id="element-2828" data-mp="2qa5cph5decdh">
                    Retry
                  </Button>} sx={{
          mb: 2,
          borderRadius: 2
        }} data-id="element-2827" data-mp="2lbbyfmi9ehc9">
              {error}
            </Alert> : isLoading ?
        // Loading skeletons
        Array.from({
          length: 3
        }).map((_, sectionIndex) => <Box key={`section-skeleton-${sectionIndex}`} sx={{
          mb: 4
        }} data-id="element-2829" data-mp="1nkp17g9gu85k" data-mpi={sectionIndex}>
                <Skeleton width={150} height={32} sx={{
            mb: 2
          }} data-id="element-2830" data-mp="1x9gpnefbiabq" />
                <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr'
            },
            gap: 3
          }} data-id="element-2831" data-mp="1sgxf6qbxfvb5">
                  {Array.from({
              length: 4
            }).map((_, fieldIndex) => <Box key={`field-skeleton-${fieldIndex}`} data-id="element-2832" data-mp="2b7ezq7mlvklf" data-mpi={fieldIndex}>
                      <Skeleton width={100} height={20} sx={{
                mb: 1
              }} data-id="element-2833" data-mp="lvcvo88qcm6o" />
                      <Skeleton width="100%" height={24} data-id="element-2834" data-mp="2sjly8pum06i2" />
                    </Box>)}
                </Box>
              </Box>) :
        // Actual content
        sections.map((section, sectionIndex) => <Box key={`section-${sectionIndex}`} sx={{
          mb: 4
        }} data-id="element-2835" data-mp="2nrc0wor6045r" data-mpi={sectionIndex}>
                <Typography variant="subtitle1" fontWeight={600} sx={{
            mb: 2,
            color: 'primary.main'
          }} data-id="element-2836" data-mp="29ufb8qms8sqg">
                  {section.title}
                </Typography>
                <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr'
            },
            gap: 3
          }} data-id="element-2837" data-mp="24vc5f3yzaihf">
                  {section.fields.map((field, fieldIndex) => <Box key={`field-${fieldIndex}`} data-id="element-2838" data-mp="3sqr1lg1avj07" data-mpi={fieldIndex}>
                      <Typography variant="caption" color="text.secondary" sx={{
                mb: 0.5,
                display: 'block'
              }} data-id="element-2839" data-mp="3st3ehes7b36e">
                        {field.label}
                      </Typography>
                      <Typography variant="body1" data-id="element-2840" data-mp="fd5k7hocjb2t">
                        {renderFieldValue(field)}
                      </Typography>
                    </Box>)}
                </Box>
              </Box>)}
        </Box>
        {/* Footer */}
        <Box sx={{
        p: 3,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-2841" data-mp="1328blh8s8iai">
          <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isLoading} data-id="element-2842" data-mp="16l724x8edw3a">
            Close
          </Button>
          <Button variant="contained" onClick={onEdit} startIcon={<Icon icon="lucide:edit" width={18} height={18} data-id="element-2844" data-mp="1kkf23scdx88j" />} disabled={isLoading} data-id="element-2843" data-mp="1fjj39t6w4c9z">
            Edit
          </Button>
        </Box>
      </Box>
    </>;
  return isMobile ? <Dialog open={isOpen} onClose={handleClose} fullScreen TransitionProps={{
    onExited: onClose
  }} data-id="element-2845" data-mp="1tj0lfppokrad">
      <AppBar position="static" color="default" elevation={0} data-id="element-2846" data-mp="1yd4s07v1axax">
        <Toolbar data-id="element-2847" data-mp="238k5gyp4qr6t">
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close" data-id="element-2848" data-mp="2393jw5fkmxg3">
            <Icon icon="lucide:x" width={24} height={24} data-id="element-2849" data-mp="2d79myqd469mj" />
          </IconButton>
          <Typography sx={{
          ml: 2,
          flex: 1
        }} variant="h6" component="div" noWrap data-id="element-2850" data-mp="2yircf73w620z">
            {isLoading ? <Skeleton width={150} data-id="element-2851" data-mp="37jfs068xoafz" /> : title}
          </Typography>
          <Button autoFocus color="primary" onClick={onEdit} disabled={isLoading} data-id="element-2852" data-mp="37iwi9ctfssd1">
            Edit
          </Button>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{
      p: 0
    }} data-id="element-2853" data-mp="2esxnd0ska10g">
        {content}
      </DialogContent>
    </Dialog> : <Drawer anchor="right" open={isOpen} onClose={handleClose} ModalProps={{
    keepMounted: true
  }} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: '480px'
      },
      boxShadow: '0 0 24px rgba(0,0,0,0.1)'
    }
  }} data-id="element-2854" data-mp="2old8u832h7q3">
      {content}
    </Drawer>;
};
export default EntityViewDrawer;